import {
  Badge,
  confirm,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { RowActions } from "@/shared/ui/RowActions";
import { AppImage } from "@/shared/ui/AppImage";
import type { EventCategoryDTO } from "../../domain/contracts";

type EventCategoryTableProps = {
  data: EventCategoryDTO[];
  isLoading?: boolean;
  onEdit: (item: EventCategoryDTO) => void;
  onDelete: (id: string) => void | Promise<void>;
};

export function EventCategoryTable({
  data,
  isLoading,
  onEdit,
  onDelete,
}: EventCategoryTableProps) {
  const canUpdate = useActionPermission(["performance-events:update"]);
  const canDelete = canUpdate;

  const columns: TanstackReactTable.ColumnDef<EventCategoryDTO>[] = [
    {
      id: "logo",
      header: "",
      size: 56,
      cell: ({ row }) => (
        <div className="size-10 overflow-hidden rounded-md border bg-muted">
          <AppImage
            src={row.original.logoStorageKey ?? undefined}
            alt=""
            className="size-full"
            fit="contain"
          />
        </div>
      ),
    },
    {
      id: "name",
      header: "ຊື່",
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-sm">{row.original.name}</p>
          {row.original.nameEn ? (
            <p className="text-muted-foreground text-xs">{row.original.nameEn}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.slug}</span>
      ),
    },
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      cell: ({ row }) => (
        <span className="tabular-nums text-sm">{row.original.sortOrder}</span>
      ),
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
        </Badge>
      ),
    },
    {
      id: "actions",
      size: 80,
      header: "",
      cell: ({ row }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void | Promise<void>;
        }[] = [];

        if (canUpdate) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }
        if (canDelete) {
          actions.push({
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            variant: "destructive",
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຫມວດງານ",
                description: `ລຶບ "${row.original.name}"? ງານທີ່ອ້າງອີງອາດກະທົບ`,
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(row.original.id);
            },
          });
        }
        if (actions.length === 0) return null;
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<EventCategoryDTO, unknown>
      data={data}
      columns={columns}
      isLoading={isLoading}
      offset={0}
      limit={data.length || 20}
      totalCount={data.length}
      onPaginationChange={() => {}}
      noDataMessage="ບໍ່ພົບຫມວດງານ"
    />
  );
}
